import type * as React from 'react'
import { cn } from '@/shared/lib/utils'

const COMMON_DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'icloud.com',
  'live.com'
]

/**
 * Sugerencias de correo (juan@gmail.com…) a partir del valor actual. Función pura: se
 * muestran mientras se escribe la parte local + "@" y el dominio aún no tiene punto (TLD).
 */
export function getEmailSuggestions(value: string, max = 4): string[] {
  const atIndex = value.indexOf('@')
  const local = atIndex > 0 ? value.slice(0, atIndex) : ''
  const domainPart = atIndex >= 0 ? value.slice(atIndex + 1) : ''
  if (local.length === 0 || domainPart.includes('.')) return []
  return COMMON_DOMAINS.filter((d) => d.startsWith(domainPart.toLowerCase()))
    .slice(0, max)
    .map((d) => `${local}@${d}`)
}

interface EmailSuggestionsProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSelect'> {
  /** Valor actual del correo (controlado por el consumidor). */
  value: string
  /** Se llama con el correo completo al tocar un chip. */
  onSelect: (email: string) => void
  /** Máximo de chips a mostrar. */
  max?: number
}

/**
 * Chips de sugerencia de dominio para un campo de correo. Headless respecto del form:
 * recibe `value` y `onSelect`, así lo comparten el `EmailField` (RHF) y los formularios
 * con estado plano (p.ej. el wizard de alta de cliente). Cero dependencias de dominio.
 */
export function EmailSuggestions({
  value,
  onSelect,
  max = 4,
  className,
  ...props
}: EmailSuggestionsProps) {
  const suggestions = getEmailSuggestions(value, max)
  if (suggestions.length === 0) return null

  return (
    <div
      className={cn('mt-1 flex flex-wrap gap-1', className)}
      role="listbox"
      aria-label="Sugerencias de correo"
      {...props}
    >
      {suggestions.map((email) => (
        <button
          key={email}
          type="button"
          role="option"
          aria-selected={false}
          // preventDefault en mousedown: evita que el input pierda el foco al tocar el chip.
          // Sin esto, el blur dispara la validación con el correo a medio escribir (juan@gm)
          // y muestra "no es válido" aunque el click luego complete el dominio.
          onMouseDown={(e) => {
            e.preventDefault()
          }}
          onClick={() => {
            onSelect(email)
          }}
          className="rounded-full border bg-muted/50 px-2 py-0.5 text-xs text-foreground hover:bg-muted"
        >
          @{email.slice(email.indexOf('@') + 1)}
        </button>
      ))}
    </div>
  )
}
